// @flow

import React, { type Node } from 'react';

import { Spacing } from '@zapier/theme';
import { jsx } from '@emotion/core';
import {
  type PropsQueriesType,
  PropsQuery,
} from '@zapier/common-components/src/components/PropsQuery';

/** @jsx jsx */

type GutterType =
  | 'none'
  | 'normal'
  | 'extraSmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'extraLarge';
type AlignType = 'stretch' | 'flex-start' | 'flex-end' | 'center';
type JustifyType = 'space-between' | 'center' | 'flex-end' | 'flex-start';
type DirectionType = 'column' | 'row' | 'column-reverse' | 'row-reverse';
type WrapType = 'nowrap' | 'wrap' | 'wrap-reverse';

type ItemProps = {
  align: AlignType,
  children: Node,
  direction?: DirectionType,
  grow?: number,
  gutter: GutterType,
  justify: JustifyType,
  shrink?: number,
  wrap?: WrapType,
};

const getItemCss = (props: ItemProps) => ({
  flexGrow: props.grow || 0,
  flexShrink: props.shrink || 1,
  margin: Spacing[props.gutter],
});

const Item = (props: ItemProps) => (
  <div css={getItemCss(props)}>{props.children}</div>
);

const getBoxCss = (props: ItemProps) => ({
  display: 'flex',
  flexWrap: props.wrap || 'wrap',
  alignItems: props.align,
  justifyContent: props.justify,
  flexDirection: props.direction,
  // Someday, we'll column-gap and row-gap for this:
  // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Flexible_Box_Layout/Mastering_Wrapping_of_Flex_Items#Creating_gutters_between_items
  margin: -Spacing[props.gutter],
});

type Props = {
  /** Child alignment. */
  align: AlignType,
  /** Any number of text or elements, which will be evenly spaced by the gutter. */
  children: Node,
  /** Flex direction value */
  direction: DirectionType,
  /** Flex grow value */
  grow?: number,
  /** Amount of horizontal and vertical spacing between each item. */
  gutter: GutterType,
  /** Flex justify-content value */
  justify?: JustifyType,
  /** Props Queries */
  propsQueries?: PropsQueriesType,
  /** Flex shrink value */
  shrink?: number,
  /** Flex wrap value */
  wrap?: WrapType,
};

const FlexLayout = (props: Props) => (
  <PropsQuery queries={props.propsQueries}>
    {overrides => (
      <div css={getBoxCss({ ...props, ...overrides })}>
        {React.Children.map(props.children, child => (
          <Item {...props} {...overrides}>
            {child}
          </Item>
        ))}
      </div>
    )}
  </PropsQuery>
);

FlexLayout.defaultProps = {
  align: 'stretch',
  direction: 'row',
  gutter: 'normal',
};

export default FlexLayout;
